import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { SiteFooter } from "@/components/SiteFooter";

export default function FreeSignupPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError("Enter a real email so we have somewhere to send it.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/free-signup", { email: email.trim() });
      setSuccess(true);
      setAlreadySubscribed(Boolean(r.data?.already_subscribed));
    } catch (err) {
      const detail =
        err?.response?.data?.detail ??
        "Something went sideways. Try again in a second.";
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-brand-paper text-brand-black min-h-screen flex flex-col">
      <Nav />
      <main className="flex-1 px-6 py-24 md:py-32">
        <div className="max-w-xl mx-auto">
          <div className="text-center">
            <p
              className="font-mono text-[11px] font-bold tracking-[0.28em] uppercase text-brand-red"
              data-testid="free-signup-eyebrow"
            >
              Free Weekly
            </p>
            <h1
              className="mt-5 font-serif font-black text-display-lg text-brand-black leading-[1.1]"
              data-testid="free-signup-headline"
            >
              Get the Free Weekly
            </h1>
            <p className="mt-5 text-base md:text-lg text-brand-muted-strong leading-relaxed">
              Start free. One email a week with everything that mattered.
              Friday morning, five-minute read, no card required.
            </p>
          </div>

          {success ? (
            <div
              className="mt-12 bg-white border border-brand-black shadow-hard p-8 md:p-10 text-center"
              data-testid="free-signup-success"
            >
              <p className="font-mono text-[11px] font-bold tracking-[0.28em] uppercase text-brand-red">
                {alreadySubscribed ? "Already in" : "You're in"}
              </p>
              <p className="mt-4 font-serif text-2xl font-black text-brand-black">
                {alreadySubscribed
                  ? "You were already on the list."
                  : "Check your inbox for your welcome email."}
              </p>
              <p className="mt-4 text-base text-brand-muted-strong leading-relaxed">
                Your first weekly briefing lands Friday morning. Want your
                team every single morning instead? The daily plans are one
                click away.
              </p>
              <div className="mt-7">
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3.5 font-semibold text-base hover:bg-brand-black transition-colors duration-200"
                  data-testid="free-signup-success-upgrade-cta"
                >
                  See the daily plans →
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="mt-11 flex flex-col gap-4"
              data-testid="free-signup-form"
            >
              <label
                htmlFor="free-signup-email"
                className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-brand-black"
              >
                Your email
              </label>
              <input
                id="free-signup-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={submitting}
                className="w-full border border-brand-black/25 bg-white px-4 py-4 text-base text-brand-black placeholder:text-brand-muted-soft focus:outline-none focus:border-brand-red transition-colors disabled:opacity-60"
                data-testid="free-signup-email-input"
              />
              {error ? (
                <p
                  className="text-sm text-brand-red font-semibold"
                  data-testid="free-signup-error"
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={submitting}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-black text-white px-6 py-4 font-semibold text-base shadow-hard-sm hover:bg-brand-red transition-colors duration-200 disabled:opacity-60"
                data-testid="free-signup-submit"
              >
                {submitting ? "Signing you up..." : "Sign Up Free"}
              </button>
              <p className="mt-3 text-center font-mono text-xs tracking-[0.06em] text-brand-muted">
                Free forever. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
